import {DataTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Box, Text, type BoxProps} from '@mantine/core';
import React, {ReactNode} from 'react';

const FIELD_LABEL_SX = {margin: '4px 0'} as const;

const FIELD_LABEL_WITH_TOP_SPACING_SX = {
  ...FIELD_LABEL_SX,
  paddingTop: 10,
} as const;

const FIELD_VALUE_SX = {
  padding: '6px 12px',
  background: Color.GRAY_200,
  color: Color.GRAY_600,
  borderRadius: 4,
  overflowWrap: 'anywhere' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-start' as const,
  boxSizing: 'border-box' as const,
};

export type MoleculeFieldValueProps = {children: ReactNode} & Omit<BoxProps, 'children' | 'sx'> &
  DataTestProps;

export function MoleculeInaccessibleMessage(): JSX.Element {
  return (
    <Text component="div">
      You do not have access to view this molecule. Please ask your administrator for access.
    </Text>
  );
}

export function MoleculeFieldLabel(props: {children: ReactNode; withTopSpacing?: boolean}): JSX.Element {
  const {children, withTopSpacing = false} = props;
  return (
    <Text fw={700} component="div" sx={withTopSpacing ? FIELD_LABEL_WITH_TOP_SPACING_SX : FIELD_LABEL_SX}>
      {children}
    </Text>
  );
}

export function MoleculeFieldValue({children, ...rest}: MoleculeFieldValueProps): JSX.Element {
  return (
    <Box component="div" sx={FIELD_VALUE_SX} {...rest}>
      {children}
    </Box>
  );
}

export function MoleculeFieldRow(props: {
  label: ReactNode;
  children: ReactNode;
  withTopSpacing?: boolean;
  valueProps?: Omit<MoleculeFieldValueProps, 'children'>;
}): JSX.Element {
  const {label, children, withTopSpacing = false, valueProps} = props;
  return (
    <Box>
      <MoleculeFieldLabel withTopSpacing={withTopSpacing}>{label}</MoleculeFieldLabel>
      <MoleculeFieldValue {...valueProps}>{children}</MoleculeFieldValue>
    </Box>
  );
}
